/**
 * FLIP math for the route meter docking into the card's Facts (§E.1, B8).
 * Pure: rectangles in, transforms out, so it is unit-tested in Node.
 *
 * The meter's figure group scales about its left-centre and travels so that its
 * left-centre lands on the target figure's left-centre; scale = height ratio
 * (the meter figure is 22 px Newsreader, the Facts figure 26 px).
 */
export interface Rect { left: number; top: number; width: number; height: number }

export interface Flip { dx: number; dy: number; s: number }

export function flipTo(from: Rect, to: Rect): Flip {
  const s = from.height > 0 && to.height > 0 ? to.height / from.height : 1;
  return {
    dx: to.left - from.left,
    dy: to.top + to.height / 2 - (from.top + from.height / 2),
    s: Math.round(s * 1000) / 1000,
  };
}

/** CSS transform for a Flip (transform-origin must be "0 50%"). */
export const flipTransform = (f: Flip): string => `translate(${f.dx.toFixed(1)}px, ${f.dy.toFixed(1)}px) scale(${f.s})`;

/**
 * Where an element inside an animating container will sit once the container's
 * transform has settled. The card enters with translate(28px) scale(.985); the
 * dock must aim at the final position, not the first frame.
 *
 *   child      the child's current (transformed) client rect
 *   box        the container's current (transformed) client rect
 *   layoutBox  where the container sits without its transform (offset geometry)
 *   layoutW    the container's untransformed width (offsetWidth)
 */
export function settledRect(child: Rect, box: Rect, layoutBox: { left: number; top: number }, layoutW: number): Rect {
  const sc = layoutW > 0 && box.width > 0 ? box.width / layoutW : 1;
  return {
    left: layoutBox.left + (child.left - box.left) / sc,
    top: layoutBox.top + (child.top - box.top) / sc,
    width: child.width / sc,
    height: child.height / sc,
  };
}

/** True when a rect is at least partly inside the viewport (else the meter just fades). */
export function onScreen(r: Rect, vw: number, vh: number): boolean {
  return r.width > 0 && r.height > 0 && r.left < vw && r.top < vh && r.left + r.width > 0 && r.top + r.height > 0;
}
